import yaml from 'js-yaml';

/** Frontmatter keys the Properties panel treats as system / internal. */
export const RESERVED_PROPERTY_KEYS = [
  'created_by',
  'property_types',
] as const;

export type ReservedPropertyKey = (typeof RESERVED_PROPERTY_KEYS)[number];

export const CREATED_BY_KEY = 'created_by' as const;
export const PROPERTY_TYPES_KEY = 'property_types' as const;

export type PropertyType =
  | 'text'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'list'
  | 'tags';

export const PROPERTY_TYPES: readonly PropertyType[] = [
  'text',
  'number',
  'checkbox',
  'date',
  'datetime',
  'list',
  'tags',
];

export type FrontmatterProperties = Record<string, unknown>;

export type ParseFrontmatterResult =
  | { ok: true; properties: FrontmatterProperties }
  | { ok: false; properties: FrontmatterProperties; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isReservedPropertyKey(key: string): boolean {
  return (RESERVED_PROPERTY_KEYS as readonly string[]).includes(key);
}

/**
 * Parse the YAML body stored inside a frontmatter node (no `---` fences).
 * Empty / whitespace-only → empty map. Malformed YAML keeps an empty map and
 * reports an error so the UI can warn without rewriting the note body.
 */
export function parseFrontmatterYaml(text: string): ParseFrontmatterResult {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: true, properties: {} };
  }

  try {
    // CORE_SCHEMA keeps date-like scalars as strings so the Properties date
    // editors receive `YYYY-MM-DD` text instead of JS Date objects.
    const loaded = yaml.load(trimmed, { schema: yaml.CORE_SCHEMA });
    if (loaded == null) {
      return { ok: true, properties: {} };
    }
    if (!isPlainObject(loaded)) {
      return {
        ok: false,
        properties: {},
        error: 'Frontmatter must be a YAML mapping of keys to values.',
      };
    }
    return { ok: true, properties: loaded };
  } catch (error) {
    return {
      ok: false,
      properties: {},
      error:
        error instanceof Error
          ? error.message
          : 'Frontmatter could not be parsed as YAML.',
    };
  }
}

/**
 * Serialize a properties map to the YAML body for a frontmatter node.
 * Dates that arrived as `Date` objects are written as ISO strings.
 */
export function serializeFrontmatterYaml(
  properties: FrontmatterProperties,
): string {
  const cleaned: FrontmatterProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) {
      continue;
    }
    cleaned[key] = value instanceof Date ? value.toISOString() : value;
  }

  if (Object.keys(cleaned).length === 0) {
    return '';
  }

  return yaml
    .dump(cleaned, {
      lineWidth: -1,
      noRefs: true,
      sortKeys: false,
      schema: yaml.CORE_SCHEMA,
    })
    .replace(/\n$/, '');
}

/** Custom (editable) keys only — reserved keys are filtered out. */
export function customPropertyKeys(
  properties: FrontmatterProperties,
): string[] {
  return Object.keys(properties).filter((key) => !isReservedPropertyKey(key));
}

export function readPropertyTypes(
  properties: FrontmatterProperties,
): Record<string, PropertyType> {
  const raw = properties[PROPERTY_TYPES_KEY];
  if (!isPlainObject(raw)) {
    return {};
  }
  const result: Record<string, PropertyType> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (
      typeof value === 'string' &&
      (PROPERTY_TYPES as readonly string[]).includes(value)
    ) {
      result[key] = value as PropertyType;
    }
  }
  return result;
}

export function withPropertyTypes(
  properties: FrontmatterProperties,
  types: Record<string, PropertyType>,
): FrontmatterProperties {
  const next = { ...properties };
  if (Object.keys(types).length === 0) {
    delete next[PROPERTY_TYPES_KEY];
  } else {
    next[PROPERTY_TYPES_KEY] = types;
  }
  return next;
}

export function readCreatedBy(
  properties: FrontmatterProperties,
): string | undefined {
  const value = properties[CREATED_BY_KEY];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
}

/** Build the `created_by` display string stamped into new notes. */
export function formatCreatedByLabel(input: {
  displayName?: string | null;
  email?: string | null;
}): string | undefined {
  const name = input.displayName?.trim() || undefined;
  const email = input.email?.trim() || undefined;
  if (name && email) {
    return `${name} <${email}>`;
  }
  return name ?? email;
}

/** Markdown seed for a brand-new note, or empty string when there is no author. */
export function noteSeedWithCreatedBy(createdBy: string | undefined): string {
  if (!createdBy) {
    return '';
  }
  const body = serializeFrontmatterYaml({ [CREATED_BY_KEY]: createdBy });
  return `---\n${body}\n---\n\n`;
}

/**
 * YAML body inside a leading `---` fenced frontmatter block, or `''` when the
 * document has none. Does not include the fence lines.
 */
export function extractFrontmatterYamlFromMarkdown(markdown: string): string {
  const text = markdown.replace(/^\uFEFF/, '');
  const match = text.match(/^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/);
  return match?.[1] ?? '';
}
